import type {
  AcknowledgementStatus,
  CriticalNotification,
  CriticalNotificationDetailContext,
  NotificationStatus,
} from '../types/criticalNotification.types';
import type { CriticalResultsDashboardAlert } from '../types/criticalResultsDashboard.types';

export function notificationStatusLabel(status?: NotificationStatus | string | null): string {
  switch (status) {
    case 'Pending':
      return 'Pending';
    case 'Published':
      return 'Published';
    case 'Failed':
      return 'Delivery Failed';
    case 'Cancelled':
      return 'Cancelled';
    default:
      return status ? String(status) : '—';
  }
}

export function notificationStatusClass(status?: NotificationStatus | string | null): string {
  switch (status) {
    case 'Failed':
      return 'crd-status crd-status--breached';
    case 'Published':
      return 'crd-status crd-status--acknowledged';
    case 'Pending':
      return 'crd-status crd-status--pending';
    case 'Cancelled':
      return 'crd-status crd-status--closed';
    default:
      return 'crd-status';
  }
}

export function acknowledgementStatusLabel(status?: AcknowledgementStatus | string | null): string {
  switch (status) {
    case 'Pending':
      return 'Pending Acknowledgement';
    case 'Acknowledged':
      return 'Acknowledged';
    case 'NotRequired':
      return 'Not Required';
    case 'Cancelled':
      return 'Cancelled';
    default:
      return status ? String(status) : '—';
  }
}

export function acknowledgementStatusClass(status?: AcknowledgementStatus | string | null): string {
  switch (status) {
    case 'Pending':
      return 'crd-status crd-status--pending';
    case 'Acknowledged':
      return 'crd-status crd-status--acknowledged';
    case 'Cancelled':
      return 'crd-status crd-status--closed';
    default:
      return 'crd-status crd-status--closed';
  }
}

export function canAcknowledgeNotification(notification: CriticalNotification | null): boolean {
  if (!notification) {
    return false;
  }
  if (notification.acknowledgementStatus !== 'Pending') {
    return false;
  }
  if (notification.notificationStatus === 'Cancelled') {
    return false;
  }
  const instanceStatus = notification.escalationInstance?.status;
  return !instanceStatus || instanceStatus === 'Active';
}

export function buildDetailContextFromAlert(alert: CriticalResultsDashboardAlert): {
  facilityId: number;
  escalationInstanceId: number;
  criticalNotificationId?: number | null;
  accessionLabel: string;
  studyLabel: string;
  patientName?: string | null;
  patientUhid?: string | null;
  orderingClinician?: string | null;
  risVisitId?: string | null;
} {
  const accession = alert.study?.accessionNumber?.trim() || alert.study?.risVisitId?.trim() || '—';
  const study =
    alert.study?.studyDisplayName?.trim() ||
    alert.study?.testName?.trim() ||
    alert.study?.studyDescription?.trim() ||
    alert.study?.modalityName?.trim() ||
    '—';

  return {
    facilityId: alert.facilityId,
    escalationInstanceId: alert.escalationInstanceId ?? alert.id,
    criticalNotificationId: alert.criticalNotificationId ?? null,
    accessionLabel: accession,
    studyLabel: study,
    patientName: alert.study?.patientName?.trim() || null,
    patientUhid: alert.study?.patientUhid?.trim() || null,
    orderingClinician: alert.study?.referredBy?.trim() || null,
    risVisitId: alert.study?.risVisitId?.trim() || alert.study?.accessionNumber?.trim() || null,
  };
}

function isPlaceholderLabel(value?: string | null): boolean {
  const trimmed = value?.trim();
  return !trimmed || trimmed === '—';
}

function firstResolvedLabel(...candidates: Array<string | null | undefined>): string | null {
  for (const candidate of candidates) {
    const trimmed = candidate?.trim();
    if (trimmed && !isPlaceholderLabel(trimmed)) {
      return trimmed;
    }
  }
  return null;
}

export function resolveNotificationAccessionLabel(
  context?: CriticalNotificationDetailContext | null,
  notification?: CriticalNotification | null
): string {
  const fromNotification =
    notification?.study?.accessionNumber?.trim() ||
    notification?.patient?.risVisitId?.trim() ||
    notification?.study?.risVisitId?.trim() ||
    null;

  return (
    firstResolvedLabel(fromNotification, context?.risVisitId, context?.accessionLabel) ??
    'Notification details'
  );
}

export function resolveNotificationStudyLabel(
  context?: CriticalNotificationDetailContext | null,
  notification?: CriticalNotification | null
): string {
  const fromNotification =
    notification?.study?.studyDisplayName?.trim() ||
    notification?.study?.testName?.trim() ||
    notification?.study?.studyDescription?.trim() ||
    notification?.study?.modalityName?.trim() ||
    null;

  return firstResolvedLabel(fromNotification, context?.studyLabel) ?? '';
}

export function resolveNotificationPatientName(
  context?: CriticalNotificationDetailContext | null,
  notification?: CriticalNotification | null
): string {
  const fromNotification =
    notification?.patient?.name?.trim() || notification?.study?.patientName?.trim() || null;

  return firstResolvedLabel(fromNotification, context?.patientName) ?? '—';
}

export function resolveNotificationPatientUhid(
  context?: CriticalNotificationDetailContext | null,
  notification?: CriticalNotification | null
): string | null {
  const fromNotification =
    notification?.patient?.patientDicomId?.trim() ||
    notification?.study?.patientUhid?.trim() ||
    null;

  if (fromNotification && !isPlaceholderLabel(fromNotification)) {
    return fromNotification;
  }
  const patientUhid = context?.patientUhid;
  if (patientUhid && !isPlaceholderLabel(patientUhid)) {
    return patientUhid.trim();
  }
  return null;
}

export function resolveNotificationOrderingClinician(
  context?: CriticalNotificationDetailContext | null,
  notification?: CriticalNotification | null
): string {
  const fromNotification = notification?.orderingClinician?.trim() || null;
  return firstResolvedLabel(fromNotification, context?.orderingClinician) ?? '—';
}

export function formatNotificationTimestamp(value?: string | null): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString();
}

function isWhitespaceChar(ch: string): boolean {
  return ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r';
}

function trimLeadingWhitespace(value: string): string {
  let index = 0;
  while (index < value.length && isWhitespaceChar(value[index])) {
    index += 1;
  }
  return value.slice(index);
}

function isDigitChar(ch: string): boolean {
  return ch >= '0' && ch <= '9';
}

function startsWithIgnoreCase(value: string, prefix: string): boolean {
  return value.toLowerCase().startsWith(prefix.toLowerCase());
}

function skipLeadingStudyReference(value: string): string {
  if (!startsWithIgnoreCase(value, 'study #')) {
    return value;
  }

  let index = 'study #'.length;
  while (index < value.length && isDigitChar(value[index])) {
    index += 1;
  }
  return trimLeadingWhitespace(value.slice(index));
}

function skipLeadingDottedNumericToken(value: string): string {
  let index = 0;
  let segmentCount = 0;

  while (index < value.length) {
    if (!isDigitChar(value[index])) {
      break;
    }

    while (index < value.length && isDigitChar(value[index])) {
      index += 1;
    }
    segmentCount += 1;

    if (index >= value.length || value[index] !== '.') {
      break;
    }
    index += 1;
  }

  if (segmentCount >= 2) {
    return trimLeadingWhitespace(value.slice(index));
  }

  return value;
}

function normalizeEscalationPrefix(value: string): string {
  const prefixes = [
    { token: 'critical escalation:', label: 'Critical escalation' },
    { token: 'reminder:', label: 'Reminder' },
  ];

  for (const prefix of prefixes) {
    if (!startsWithIgnoreCase(value, prefix.token)) {
      continue;
    }

    let rest = trimLeadingWhitespace(value.slice(prefix.token.length));
    rest = skipLeadingStudyReference(rest);
    rest = skipLeadingDottedNumericToken(rest);
    return `${prefix.label}: ${rest}`;
  }

  return value;
}

function isAlphanumericChar(ch: string): boolean {
  const code = ch.codePointAt(0);
  if (code === undefined) {
    return false;
  }
  return (code >= 48 && code <= 57) || (code >= 65 && code <= 90) || (code >= 97 && code <= 122);
}

function isDottedUidToken(token: string): boolean {
  if (!token.includes('.')) {
    return false;
  }

  const segments = token.split('.');
  if (segments.length < 3) {
    return false;
  }

  return segments.every(segment => segment.length > 0 && [...segment].every(isDigitChar));
}

function removeStudyReferences(value: string): string {
  const chunks: string[] = [];
  let index = 0;

  while (index < value.length) {
    const slice = value.slice(index);
    if (startsWithIgnoreCase(slice, 'study #')) {
      let cursor = index + 'study #'.length;
      while (cursor < value.length && isDigitChar(value[cursor])) {
        cursor += 1;
      }
      index = cursor;
      continue;
    }

    chunks.push(value[index]);
    index += 1;
  }

  return chunks.join('');
}

function removeDottedUidTokens(value: string): string {
  const tokens: string[] = [];
  let current = '';

  const flushToken = (trailingWhitespace = '') => {
    if (!current) {
      if (trailingWhitespace) {
        tokens.push(trailingWhitespace);
      }
      return;
    }

    if (isDottedUidToken(current)) {
      tokens.push(trailingWhitespace);
    } else {
      tokens.push(current + trailingWhitespace);
    }
    current = '';
  };

  for (const ch of value) {
    if (isWhitespaceChar(ch)) {
      flushToken(ch);
      continue;
    }

    if (!isAlphanumericChar(ch) && current) {
      flushToken();
      tokens.push(ch);
      continue;
    }

    if (isAlphanumericChar(ch) || ch === '.') {
      current += ch;
      continue;
    }

    flushToken();
    tokens.push(ch);
  }

  flushToken();
  return tokens.join('');
}

function collapseWhitespace(value: string): string {
  const chunks: string[] = [];
  let pendingSpace = false;

  for (const ch of value) {
    if (isWhitespaceChar(ch)) {
      pendingSpace = chunks.length > 0;
      continue;
    }

    if (pendingSpace) {
      chunks.push(' ');
      pendingSpace = false;
    }
    chunks.push(ch);
  }

  return chunks.join('');
}

function collapseColonSpacing(value: string): string {
  const chunks: string[] = [];
  let index = 0;

  while (index < value.length) {
    if (value[index] === ':') {
      chunks.push(':');
      index += 1;
      while (index < value.length && isWhitespaceChar(value[index])) {
        index += 1;
      }
      if (index < value.length) {
        chunks.push(' ');
      }
      continue;
    }

    chunks.push(value[index]);
    index += 1;
  }

  return chunks.join('');
}

/** Strip study UID / study id from escalation SMS messages for modal timeline display. */
export function simplifyEscalationEventMessage(message?: string | null): string {
  const trimmed = message?.trim();
  if (!trimmed) {
    return '—';
  }

  let result = normalizeEscalationPrefix(trimmed);
  result = removeDottedUidTokens(removeStudyReferences(result));
  result = collapseColonSpacing(collapseWhitespace(result)).trim();

  return result || '—';
}

const REMINDER_EVENT_TYPES = new Set(['remindersent', 'remindernotification', 'reminder']);

const ESCALATION_EVENT_TYPES = new Set([
  'started',
  'leveladvanced',
  'contactskipped',
  'completed',
  'cancelled',
  'escalation',
  'notificationsent',
  'acknowledged',
]);

function eventTypeKey(event: { eventType?: string | null; eventCategory?: string | null }): string {
  return String(event.eventType || event.eventCategory || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
}

/** Reminder tab: ReminderSent / reminder-category events only. */
export function filterReminderHistoryEvents<
  T extends { eventType?: string | null; eventCategory?: string | null },
>(events: T[]): T[] {
  return events.filter(event => REMINDER_EVENT_TYPES.has(eventTypeKey(event)));
}

/**
 * Escalation tab: started / advanced / skipped / escalation notifications & acks.
 * Excludes pure notification-created rows that are not escalation-runtime events.
 */
export function filterEscalationHistoryEvents<
  T extends {
    eventType?: string | null;
    eventCategory?: string | null;
    actor?: string | null;
    contactId?: number | null;
  },
>(events: T[]): T[] {
  return events.filter(event => {
    const key = eventTypeKey(event);
    if (!ESCALATION_EVENT_TYPES.has(key)) {
      return false;
    }

    // Pure notification-ack without an escalation contact belongs in timeline only
    if (key === 'acknowledged') {
      const actor = String(event.actor || '').toLowerCase();
      return actor.includes('escalation') || event.contactId != null;
    }

    return true;
  });
}

/** Chronological order (oldest first) for timeline display. */
export function sortHistoryEventsChronologically<T extends { createdAt?: string | null }>(
  events: T[]
): T[] {
  return [...events].sort((a, b) => {
    const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return aTime - bTime;
  });
}
